/**
 * UnmarkDataPlateButton — "Not a data plate": takes back a tag made with "This is a data plate" (ISS-612), for the person who
 * clicked it by mistake. Shown only while the tag is one that button made (categoryBy is set) and no component is tied to the
 * photo yet; once a component is chosen the plate is in use and the office decides (the server refuses too, as the authority).
 */
import { Undo2 } from 'lucide-react';
import { useIsOffline } from '../../../hooks/useIsOffline';
import { useUnmarkDataPlate } from '../../../hooks/fieldService/useUnmarkDataPlate';
import type { GeneratorPhoto } from '../../../types/fieldService/generatorPhotos';

interface UnmarkDataPlateButtonProps {
  photo: GeneratorPhoto;
  generatorDocId: string;
}

export function UnmarkDataPlateButton({ photo, generatorDocId }: UnmarkDataPlateButtonProps) {
  const takeBackable = photo.state === 'gallery' && photo.category === 'data-plate' && photo.categoryBy != null && photo.component == null;
  return takeBackable ? <UnmarkButton key={photo.id} photo={photo} generatorDocId={generatorDocId} /> : null;
}

function UnmarkButton({ photo, generatorDocId }: UnmarkDataPlateButtonProps) {
  const unmark = useUnmarkDataPlate(generatorDocId);
  const offline = useIsOffline();
  return (
    <button
      type="button"
      onClick={() => unmark.mutate({ photoId: photo.id })}
      disabled={offline || unmark.isPending}
      title={offline ? 'You are offline: saving needs a connection' : 'Take back the data-plate tag'}
      className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 text-sm text-white transition-colors hover:bg-white/20 disabled:opacity-50"
    >
      <Undo2 className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{unmark.isPending ? 'Saving...' : 'Not a data plate'}</span>
    </button>
  );
}
